import CloseIcons from '@/icons/CloseIcons'
import FilterAltIcons from '@/icons/FilterAltIcons'
import SearchIcons from '@/icons/SearchIcons'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal, TextField } from 'vnetwork-ui'
import { useGlobalSearch } from '../../hooks/useGlobalSearch'
import { SearchResultType, SearchResultWrapLine } from '../../types'
import PopularSearches from '../PopularSearches'
import RecentSearches from '../RecentSearches'
import ScopeSelector from '../ScopeSelector'
import SearchResultsList from '../SearchResultsList'
import SearchSuggestions from '../SearchSuggestions'
import { useNavigate } from 'react-router-dom'
import { useTypingPlaceholder } from '@/hooks/useTypingPlaceholder'

interface SearchBoxProps {
    placeholder?: string | string[]
    showFilters?: boolean
    className?: string
}

const SearchBox = ({
    placeholder,
    showFilters = false,
    className = '',
}: SearchBoxProps) => {
    const { t } = useTranslation('search')
    const [isOpen, setIsOpen] = useState(false)
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
    const [isRecentSearchLoading, setIsRecentSearchLoading] = useState(false)
    const [isSearchFocused, setIsSearchFocused] = useState(false)
    const searchRef = useRef<HTMLDivElement>(null)
    const modalInputRef = useRef<HTMLInputElement>(null)
    const navigate = useNavigate()

    // Auto focus modal input when modal opens
    useEffect(() => {
        if (isOpen && modalInputRef.current) {
            // Small delay to ensure modal is fully rendered
            setTimeout(() => {
                modalInputRef.current?.focus()
            }, 100)
        }
    }, [isOpen])

    // Handle typing placeholder animation
    const placeholderArray = Array.isArray(placeholder)
        ? placeholder
        : placeholder
          ? [placeholder]
          : [t('search-placeholder')]

    const typingPlaceholder = useTypingPlaceholder({
        placeholders: placeholderArray,
        typingSpeed: 80,
        deletingSpeed: 40,
        pauseDuration: 2000,
    })

    const {
        query,
        setQuery,
        results,
        isLoading,
        selectedScopes,
        recentSearches,
        updateScopes,
        handleSearch,
        handleRecentSearchClick,
        saveRecentSearch,
        total,
    } = useGlobalSearch({
        autoSearch: true,
        debounceDelay: 1000,
    })

    // Close modal when pressing Escape
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
                setShowAdvancedFilters(false)
                setIsSearchFocused(false)
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    // Determine what to show (balanced suggestions vs results)
    const showResults = query.length >= 2 && results.length > 0
    const showSuggestions =
        isSearchFocused &&
        query.length >= 2 &&
        results.length === 0 &&
        !isLoading
    const showRecentAndPopular = query.length === 0
    const showLoading = isLoading && query.length >= 2 && results.length === 0

    const handleInputChange = useCallback(
        (value: string) => {
            setQuery(value)
            setIsOpen(true)
            if (value.length >= 1) {
                setIsSearchFocused(true)
            }
        },
        [setQuery]
    )

    const handleInputFocus = useCallback(() => {
        setIsOpen(true)
        setIsSearchFocused(true)
    }, [])

    const handleInputBlur = useCallback(() => {
        // Delay to allow clicks on suggestions/results
        setTimeout(() => {
            setIsSearchFocused(false)
        }, 200)
    }, [])

    const handleKeyPress = useCallback(
        async (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && query.length >= 2) {
                console.log('🔍 SearchBox: Manual search via Enter key:', query)
                try {
                    await handleSearch(query)
                    setIsSearchFocused(false)
                } catch (error) {
                    console.error('Search failed:', error)
                }
            }
        },
        [query, handleSearch]
    )

    const handleSuggestionClick = useCallback(
        async (suggestion: string) => {
            console.log('🔍 SearchBox: Suggestion clicked:', suggestion)
            setQuery(suggestion)
            setIsSearchFocused(false)
            try {
                await handleSearch(suggestion)
            } catch (error) {
                console.error('Suggestion search failed:', error)
            }
        },
        [setQuery, handleSearch]
    )

    const handleRecentClick = useCallback(
        async (searchQuery: string) => {
            console.log('🔍 SearchBox: Recent search clicked:', searchQuery)
            setIsRecentSearchLoading(true)
            setQuery(searchQuery)
            setIsSearchFocused(false)
            try {
                await handleRecentSearchClick(searchQuery)
            } catch (error) {
                console.error('Recent search failed:', error)
            } finally {
                setIsRecentSearchLoading(false)
            }
        },
        [setQuery, handleRecentSearchClick]
    )

    const handlePopularClick = useCallback(
        async (searchQuery: string) => {
            console.log('🔍 SearchBox: Popular search clicked:', searchQuery)
            setQuery(searchQuery)
            setIsSearchFocused(false)
            try {
                await handleSearch(searchQuery)
                await saveRecentSearch(searchQuery)
            } catch (error) {
                console.error('Popular search failed:', error)
            }
        },
        [setQuery, handleSearch, saveRecentSearch]
    )

    const handleResultClick = (result: SearchResultWrapLine) => {
        setIsOpen(false)
        // setQuery('')
        setIsSearchFocused(false)
        navigate(result.redirect_to || '')
    }

    const handleBackdropClick = useCallback(() => {
        setIsOpen(false)
        setShowAdvancedFilters(false)
        setIsSearchFocused(false)
    }, [])

    const handleToggleFilters = useCallback(() => {
        setShowAdvancedFilters((prev) => !prev)
    }, [])

    const handleScopeChange = useCallback(
        (newScopes: SearchResultType[]) => {
            updateScopes(newScopes)
        },
        [updateScopes]
    )

    // Memoize SearchModal content based on current state
    const SearchModalContent = useMemo(() => {
        if (showResults) {
            return (
                <SearchResultsList
                    query={query}
                    results={results}
                    recentSearches={recentSearches}
                    isLoading={isLoading || isRecentSearchLoading}
                    total={total}
                    onResultClick={handleResultClick}
                    onRecentSearchClick={handleRecentClick}
                />
            )
        }

        if (showLoading) {
            return (
                <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">
                        {t('searching-for')} "{query}"...
                    </p>
                </div>
            )
        }

        if (showSuggestions) {
            return (
                <SearchSuggestions
                    query={query}
                    onSuggestionClick={handleSuggestionClick}
                />
            )
        }

        if (showRecentAndPopular) {
            return (
                <div className="max-h-[400px] overflow-y-auto">
                    {/* Recent Searches Section */}
                    <RecentSearches
                        onSearchClick={handleRecentClick}
                        maxItems={5}
                        showHeader={true}
                        showClearAll={false}
                    />

                    {/* Popular Searches Section */}
                    <div className="border-t border-gray-100">
                        <PopularSearches
                            onSearchClick={handlePopularClick}
                            maxItems={6}
                            showHeader={true}
                        />
                    </div>
                </div>
            )
        }

        // Empty state
        return (
            <div className="p-8 text-center">
                <SearchIcons className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">{t('start-typing-to-search')}</p>

                {/* Quick search suggestions when no data */}
                <div className="mt-4">
                    <p className="text-sm text-gray-400 mb-2">
                        {t('try-searching-for')}:
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {['customer', 'service', 'contract'].map((term) => (
                            <Button
                                variant="outlined"
                                key={term}
                                onClick={() => handlePopularClick(term)}
                                className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1 rounded-full transition-colors cursor-pointer"
                                size="small"
                                endIcon={
                                    <SearchIcons className="text-gray-500" />
                                }
                            >
                                {term}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
        )
    }, [
        showResults,
        showLoading,
        showSuggestions,
        showRecentAndPopular,
        query,
        results,
        recentSearches,
        isLoading,
        isRecentSearchLoading,
        total,
        handleResultClick,
        handleRecentClick,
        handleSuggestionClick,
        handlePopularClick,
        t,
    ])

    // Memoize SearchModal to prevent unnecessary re-renders
    const SearchModal = useMemo(
        () => (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{
                        duration: 0.3,
                        ease: [0.4, 0, 0.2, 1],
                    }}
                    className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden"
                >
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('search')}
                        </h3>
                        <div className="flex items-center gap-2">
                            <FilterAltIcons
                                className={`w-5 h-5 cursor-pointer transition-colors ${
                                    showAdvancedFilters
                                        ? 'text-blue-600'
                                        : 'text-primary hover:text-blue-600'
                                }`}
                                onClick={handleToggleFilters}
                            />
                            <CloseIcons
                                className="w-5 h-5 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={handleBackdropClick}
                            />
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    {showAdvancedFilters && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-b border-gray-200 bg-gray-50 overflow-hidden"
                        >
                            <div className="p-4">
                                <div className="space-y-4">
                                    <ScopeSelector
                                        selectedScopes={selectedScopes}
                                        onScopeChange={handleScopeChange}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Search Input */}
                    <div className="p-4 border-b border-gray-200">
                        <TextField
                            ref={modalInputRef}
                            placeholder={typingPlaceholder}
                            value={query}
                            onChange={(e) => handleInputChange(e.target.value)}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyPress={handleKeyPress}
                            size="small"
                            className="w-full border border-gray-300 rounded-lg placeholder:text-gray-500 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            endIcon={<SearchIcons className="text-gray-500" />}
                        />
                    </div>

                    {/* Dynamic Content */}
                    <div className="flex-1 overflow-hidden">
                        {SearchModalContent}
                    </div>
                </motion.div>
            </AnimatePresence>
        ),
        [
            t,
            showFilters,
            showAdvancedFilters,
            handleToggleFilters,
            handleBackdropClick,
            typingPlaceholder,
            query,
            handleInputChange,
            handleInputFocus,
            handleInputBlur,
            handleKeyPress,
            selectedScopes,
            handleScopeChange,
            SearchModalContent,
        ]
    )

    return (
        <>
            <div ref={searchRef} className={`relative ${className}`}>
                <div className="relative">
                    <TextField
                        placeholder={typingPlaceholder}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onFocus={handleInputFocus}
                        onClick={() => setIsOpen(true)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        className="w-full border-none bg-body rounded-[8px] placeholder:text-gray-9e text-gray-9e cursor-pointer"
                        endIcon={<SearchIcons className="text-gray-9e" />}
                        readOnly
                    />
                </div>
            </div>
            {isOpen && (
                <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
                    <div className="mx-auto flex mt-10 justify-center">
                        {SearchModal}
                    </div>
                </Modal>
            )}
        </>
    )
}

export default SearchBox
